import React from "react";
import { useViewContent } from "@/lib/pixel";
import { ShieldCheck, Users, Handshake, BarChart3, MapPin, Check } from "lucide-react";
import { PageHero, Reveal, Stagger, StaggerItem, Overline, SectionHeading, CTAButton, ArrowLink } from "@/components/primitives";
import { PASSES } from "@/data/site";
import { cn } from "@/lib/utils";

const VALUE = [
  { icon: ShieldCheck, t: "Policy clarity", d: "Read E30, flex-fuel and SAF before it filters down." },
  { icon: Users, t: "The buyers", d: "Direct lines to OMC buyers, producers and investors." },
  { icon: Handshake, t: "The partners", d: "Feedstock, technology and JV partners you can sign with." },
  { icon: BarChart3, t: "The intelligence", d: "Global market reads you can act on Monday." },
];

export default function Attend() {
  useViewContent("Pricing & Passes");
  return (
    <>
      <PageHero
        overline="Attend"
        title="Walk in with questions. Walk out with deals."
        subtitle="Two days that pay for themselves in one introduction."
        image="/assets/site/attend-vip-arrival.jpg"
        imageFallback="https://images.unsplash.com/photo-1566073771259-6a8506099945?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
      >
        <CTAButton to="/register">Register Now</CTAButton>
      </PageHero>

      <section className="bg-parchment py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <SectionHeading overline="Why it's worth your time" title="The roadmap past E20 is being written now — by the people in this room." />
          <Stagger className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {VALUE.map((v) => (
              <StaggerItem key={v.t}>
                <div className="flex h-full flex-col rounded-2xl border border-border bg-white p-7 shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald2/10 text-emeraldDeep"><v.icon className="h-6 w-6" /></span>
                  <h3 className="mt-5 font-display text-lg font-bold text-navy">{v.t}</h3>
                  <p className="mt-2 text-sm text-navy/60">{v.d}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="border-y border-border bg-white py-20 md:py-28">
        <div className="mx-auto max-w-4xl px-6 md:px-12">
          <Reveal>
            <Overline className="mb-4">Who will attend</Overline>
            <h2 className="font-display text-3xl font-semibold tracking-tight text-navy sm:text-4xl">A senior room, built for deal-making.</h2>
            <p className="mt-6 text-lg leading-relaxed text-navy/70">
              Bharat Ethanol Forum draws decision-makers from across the value chain — leadership from sugar and grain ethanol producers, oil marketing companies and integrated bio-refineries; automakers building flex-fuel vehicles; distillery technology and EPC firms; investors, banks and infrastructure funds; government and policy bodies; commodity traders; sustainability specialists; and global biofuel companies that see India as their next market.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Passes */}
      <section className="bg-parchment py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <SectionHeading overline="Delegate passes" title="Pick your pass." />
          <div className="mt-14 grid gap-6 lg:grid-cols-3">
            {PASSES.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.08}>
                <div className={cn(
                  "relative flex h-full flex-col rounded-3xl border p-8 transition-all hover:-translate-y-1",
                  p.popular ? "border-ethanolOrange bg-navy text-white shadow-2xl" : "border-border bg-white shadow-sm hover:shadow-xl"
                )}>
                  {p.popular && <span className="absolute -top-3 left-8 rounded-full bg-ethanolOrange px-3 py-1 text-xs font-bold uppercase tracking-wide text-navy">Most popular</span>}
                  <h3 className={cn("font-display text-2xl font-bold", p.popular ? "text-white" : "text-navy")}>{p.name}</h3>
                  <p className={cn("mt-3 flex-1", p.popular ? "text-slate-300" : "text-navy/60")}>{p.desc}</p>
                  <p className={cn("mt-6 font-display text-2xl font-semibold", p.popular ? "text-emerald-300" : "text-emeraldDeep")}>{p.price}</p>
                  <CTAButton to="/register" variant={p.popular ? "primary" : "emerald"} className="mt-6 w-full">Register</CTAButton>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-8">
            <span className="inline-block rounded-full bg-emerald2/10 px-4 py-2 text-sm font-semibold text-emeraldDeep">Early bird pricing available — register early to save.</span>
          </Reveal>
        </div>
      </section>

      <section className="relative overflow-hidden bg-navy grain py-16">
        <div className="relative mx-auto flex max-w-4xl flex-col items-center justify-between gap-4 px-6 text-center md:flex-row md:text-left">
          <div>
            <Overline light className="mb-2">Getting there</Overline>
            <h2 className="font-display text-2xl font-bold text-white">Westin Powai Lake, Mumbai.</h2>
            <p className="mt-1 flex items-center justify-center gap-2 text-slate-300 md:justify-start"><MapPin className="h-4 w-4 text-emerald2" /> Lakeside, central Mumbai · 30–45 min from the airport.</p>
          </div>
          <ArrowLink to="/venue" light>Venue & travel details</ArrowLink>
        </div>
      </section>
    </>
  );
}
